/**
 * Exchange client for trading operations
 * Follows DexClient pattern from pkg/netrunner/clients/dex_client.go
 * Handles EIP-712 signatures and automatic satoshi conversion
 */

import { HttpClient } from '../../transport/http/HttpClient';
import { toSatoshi } from '../../utils/math';
import mormSigner from '../../signing/mormSigner';
import { ethers } from 'ethers';
import { NonceManager } from '../../utils/nonceManager';
import { InfoClient } from '../info/InfoClient';
import { shouldRefreshNonce } from '../../utils/errorDetection';
import {
  OpSubmitOrder,
  OpCancelOrder,
  OpModifyOrder,
  OpChangeLeverage,
  OpClosePosition,
  OpSwap,
  OpChangeMarginRatio,
  OpInternTransfer,
  OpBankBucketTransfer,
  OpBankExtractToBucket,
  OpBankWithdrawFromBucket,
  OpBankMint,
  OpBankTransfer,
  ExchangeClientConfig,
  PlaceOrderParams,
  CancelOrderParams,
  ModifyOrderParams,
  ChangeLeverageParams,
  ClosePositionParams,
  SwapParams,
  ChangeMarginRatioParams,
  InternTransferParams,
  BucketTransferParams,
  ExtractToBucketParams,
  WithdrawFromBucketParams,
  BankMintParams,
  BankTransferParams,
} from '../../types';

export class ExchangeClient {
  private httpClient: HttpClient;
  private infoClient: InfoClient;
  private nonceManager: NonceManager;
  private mnemonic: string;
  private chainID: number;
  private walletAddress?: string;
  private nonceCounter: number | null = null; // null = not initialized, will query on first use

  constructor(config: ExchangeClientConfig) {
    this.httpClient = new HttpClient(
      config.baseURL,
      config.maxRetries || 3
    );
    this.infoClient = new InfoClient({
      baseURL: config.baseURL,
    });
    this.nonceManager = new NonceManager(this.infoClient);
    this.mnemonic = config.mnemonic;
    this.chainID = config.chainID || 10900; // Default testnet chain ID (stored for future use)
  }

  /**
   * Initialize wallet address from mnemonic
   * Must be called before trading operations
   */
  async initialize(): Promise<void> {
    await mormSigner.initialize();
    const result = await mormSigner.generateAddress(this.mnemonic, 0);
    if (!result.success || !result.address) {
      throw new Error(`Failed to generate wallet address: ${result.error}`);
    }
    this.walletAddress = result.address;
  }

  /**
   * Get current wallet address
   */
  getWalletAddress(): string {
    if (!this.walletAddress) {
      throw new Error('ExchangeClient not initialized. Call initialize() first.');
    }
    return this.walletAddress;
  }

  /**
   * Refresh nonce from server (called on nonce errors)
   */
  private async refreshNonceFromServer(): Promise<void> {
    const walletAddress = this.getWalletAddress();
    const nonceResult = await this.nonceManager.getExpectedNextNonce(walletAddress, false);
    this.nonceCounter = nonceResult.next_nonce;
    console.log('[ExchangeClient] Refreshed nonce from server', {
      walletAddress,
      newNonce: this.nonceCounter,
    });
  }

  /**
   * Get next nonce (lazy initialization from server, then increments locally)
   * Only queries server on first use or after refresh
   */
  private async getNextNonce(): Promise<string> {
    // Lazy initialization: query server on first use
    if (this.nonceCounter === null) {
      await this.refreshNonceFromServer();
    }
    
    // Increment and return
    this.nonceCounter!++;
    return this.nonceCounter!.toString();
  }

  /**
   * Generate transaction ID
   * Follows generateTxID pattern from dex_client.go
   */
  private generateTxID(): string {
    // Generate unique transaction ID using timestamp and random component
    const timestamp = Date.now();
    const random = Math.floor(Math.random() * 1000000);
    return `tx-${timestamp}-${random}`;
  }

  /**
   * Extract private key from mnemonic in the format expected by WASM
   * Follows the same pattern as onboard.ts signOnboardMessage
   */
  private async getPrivateKeyHex(): Promise<string> {
    const walletAddress = this.getWalletAddress();
    
    // Derive wallet from mnemonic using ethers v6 HDNodeWallet with explicit BIP44 path
    const masterNode = ethers.HDNodeWallet.fromPhrase(
      this.mnemonic.trim(),
      "", // no passphrase
      "m/44'/60'/0'/0/0", // Ethereum standard BIP44 path
    );

    // Validate that derived address matches
    if (masterNode.address.toLowerCase() !== walletAddress.toLowerCase()) {
      throw new Error(
        `Address mismatch: derived ${masterNode.address} does not match ${walletAddress}`,
      );
    }

    // Extract private key in the exact format Go's crypto.ToECDSA expects
    const privateKeyBigInt = BigInt(masterNode.privateKey);
    const SECP256K1_ORDER = BigInt(
      "0xFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFEBAAEDCE6AF48A03BBFD25E8CD0364141",
    );

    if (privateKeyBigInt === 0n || privateKeyBigInt >= SECP256K1_ORDER) {
      throw new Error(
        `Invalid private key: must be between 1 and secp256k1 order - 1`,
      );
    }

    // Convert BigInt to hex string (matches Go's privKey.D.Bytes() -> hex.EncodeToString)
    const hexWithoutPrefix = privateKeyBigInt.toString(16).toLowerCase();
    const paddedHex = hexWithoutPrefix.padStart(64, "0"); // Pad to 64 hex chars (32 bytes)

    if (!/^[0-9a-f]{64}$/.test(paddedHex)) {
      throw new Error(
        `Invalid private key format: must be 64 lowercase hex characters (0-9, a-f) without 0x prefix`,
      );
    }

    return paddedHex;
  }

  /**
   * Sign payload using generateSingleSignature (same as onboard)
   * This ensures correct recovery ID format matching Go implementation
   */
  private async signPayload(payload: Record<string, any>): Promise<string> {
    const walletAddress = this.getWalletAddress();
    const privateKeyHex = await this.getPrivateKeyHex();
    const now = Math.floor(Date.now() / 1000);

    // Extract deadline from payload if present
    let deadline: number | undefined;
    if (payload.deadline) {
      deadline = parseInt(payload.deadline, 10);
    }

    // Build EIP712Tx structure for generateSingleSignature
    const eip712Tx: Record<string, any> = {
      txId: "", // Empty txId for signing
      sigType: "ECDSA_LEGACY", // Signature type as string
      threshold: 1,
      timestamp: now,
      payload: payload,
    };

    // Add deadline if present
    if (deadline !== undefined) {
      eip712Tx.deadline = deadline;
    }

    // Use generateSingleSignature to sign the EIP712Tx
    const result = await mormSigner.generateSingleSignature(
      JSON.stringify(eip712Tx),
      privateKeyHex,
      walletAddress.toLowerCase(),
    );

    if (!result.success || !result.signature) {
      throw new Error(result.error || "Failed to generate signature");
    }

    return result.signature;
  }

  /**
   * Build order submission payload following buildOrderSubmissionPayload pattern
   * Creates payload with action, marketId, side, orderType, price, quantity
   * Price and quantity are converted to satoshi strings if provided as decimals
   */
  private buildOrderPayload(params: PlaceOrderParams): Record<string, any> {
    // Convert price and quantity to satoshi strings
    const priceStr = typeof params.price === 'string' 
      ? params.price 
      : toSatoshi(params.price);
    
    const quantityStr = typeof params.quantity === 'string'
      ? params.quantity
      : toSatoshi(params.quantity);

    // Build payload following buildOrderSubmissionPayload pattern
    const payload: Record<string, any> = {
      action: OpSubmitOrder,
      marketId: params.marketId,
      side: params.side.toUpperCase(),
      orderType: params.orderType.toUpperCase(),
      price: priceStr, // Already in satoshi format
      quantity: quantityStr, // Already in satoshi format
    };

    // Add optional advanced parameters (all in satoshi format)
    if (params.leverage) {
      payload.leverage = params.leverage;
    }
    if (params.takeProfit) {
      payload.takeProfit = params.takeProfit;
    }
    if (params.stopLoss) {
      payload.stopLoss = params.stopLoss;
    }

    return payload;
  }

  /**
   * Create complete EIP-712 message with nonce, deadline, owner
   * Follows PlaceOrderMessage pattern from dex_client.go
   * Queries server for nonce on first use, then uses local counter
   */
  private async buildCompleteMessage(payload: Record<string, any>): Promise<Record<string, any>> {
    const now = Math.floor(Date.now() / 1000); // Unix timestamp in seconds
    const deadline = now + 3600; // 1 hour from now (in seconds)
    const nonce = await this.getNextNonce(); // Lazy initialization from server, then increments locally
    
    return {
      ...payload,
      owner: this.getWalletAddress(),
      nonce: nonce,
      deadline: deadline.toString(), // Unix seconds as string
    };
  }

  /**
   * Build EIP712Tx structure for Router v1 unified write endpoint
   * Follows buildEIP712Tx pattern from dex_client.go
   * Creates the complete transaction structure with txId, sigType, payload, signatures
   */
  private buildEIP712Tx(payload: Record<string, any>, signature: string): Record<string, any> {
    const now = Math.floor(Date.now() / 1000); // Unix timestamp in seconds
    
    // Extract deadline from payload if present
    let deadline: number | undefined;
    if (payload.deadline) {
      deadline = parseInt(payload.deadline, 10);
    }

    // Build EIP712Tx structure matching WriteTransactionRequest
    const eip712Tx: Record<string, any> = {
      txId: this.generateTxID(),
      sigType: 1, // ECDSA_LEGACY
      threshold: 1,
      timestamp: now,
      payload: payload,
      signatures: [{
        signer: this.getWalletAddress(),
        signature: signature,
      }],
    };

    // Add deadline if present
    if (deadline !== undefined) {
      eip712Tx.deadline = deadline;
    }

    return eip712Tx;
  }

  /**
   * Place an order with automatic satoshi conversion
   * Follows SubmitOrder pattern from dex_client.go
   * 
   * @param params - Order parameters (price/quantity can be decimal or satoshi string)
   * @returns API response
   */
  async placeOrder(params: PlaceOrderParams): Promise<any> {
    // Input validation (following Go implementation)
    if (!params.marketId || params.marketId.trim() === '') {
      throw new Error('marketId cannot be empty');
    }
    if (!params.side || (params.side.toUpperCase() !== 'BUY' && params.side.toUpperCase() !== 'SELL')) {
      throw new Error('side must be BUY or SELL');
    }
    if (!params.orderType || (params.orderType.toUpperCase() !== 'LIMIT' && params.orderType.toUpperCase() !== 'MARKET')) {
      throw new Error('orderType must be LIMIT or MARKET');
    }
    const price = typeof params.price === 'string' ? parseFloat(params.price) : params.price;
    const quantity = typeof params.quantity === 'string' ? parseFloat(params.quantity) : params.quantity;
    if (!price || price <= 0) {
      throw new Error('price must be positive');
    }
    if (!quantity || quantity <= 0) {
      throw new Error('quantity must be positive');
    }

    if (!this.walletAddress) {
      await this.initialize();
    }

    // Build payload
    const payload = this.buildOrderPayload(params);
    const completeMessage = await this.buildCompleteMessage(payload);

    // Generate EIP-712 signature using generateSingleSignature (fixes recovery ID issue)
    const signature = await this.signPayload(completeMessage);

    // Build flat HTTP request structure (following submitCLOBOrderFlat pattern)
    const timestamp = Math.floor(Date.now() / 1000) - 5; // 5 seconds in the past for clock skew
    const clientOrderId = `order-${Date.now()}-${Math.floor(Math.random() * 1000000)}`;
    const isBuy = params.side.toUpperCase() === 'BUY';

    const orderReq: Record<string, any> = {
      address: this.getWalletAddress(),
      asset_pair: params.marketId,
      market_id: params.marketId,
      price: completeMessage.price,
      size: completeMessage.quantity, // Use "size" instead of "quantity" for HTTP request
      is_buy: isBuy,
      order_type: params.orderType.toUpperCase(),
      timestamp: timestamp,
      client_order_id: clientOrderId,
      nonce: completeMessage.nonce,
      deadline: completeMessage.deadline, // Already in Unix seconds as string
      signature: signature,
      time_in_force: params.timeInForce || 'GTC',
    };

    // Add advanced parameters if provided
    if (params.leverage) {
      orderReq.leverage = params.leverage;
    }
    if (params.takeProfit) {
      orderReq.take_profit = params.takeProfit;
    }
    if (params.stopLoss) {
      orderReq.stop_loss = params.stopLoss;
    }
    if (params.postOnly) {
      orderReq.post_only = true;
    }
    if (params.hidden) {
      orderReq.hidden = true;
    }
    if (params.displayQuantity) {
      orderReq.display_quantity = params.displayQuantity;
    }
    if (params.reduceOnly) {
      orderReq.reduce_only = true;
    }

    // Submit to /clob/v1/order endpoint
    const response = await this.httpClient.post('/clob/v1/order', orderReq);
    return response.body;
  }

  /**
   * Cancel an order
   * Follows CancelOrder pattern from dex_client.go
   * Uses EIP712Tx structure for Router v1 unified write endpoint
   */
  async cancelOrder(params: CancelOrderParams): Promise<any> {
    console.log('[ExchangeClient.cancelOrder] Starting cancel order request', {
      orderId: params.orderId,
      marketId: params.marketId,
      timestamp: new Date().toISOString(),
    });

    // Input validation (following Go implementation)
    if (!params.orderId || params.orderId === '') {
      console.error('[ExchangeClient.cancelOrder] Validation failed: orderId is empty', params);
      throw new Error('orderId cannot be empty');
    }
    if (!params.marketId || params.marketId.trim() === '') {
      console.error('[ExchangeClient.cancelOrder] Validation failed: marketId is empty', params);
      throw new Error('marketId cannot be empty');
    }

    console.log('[ExchangeClient.cancelOrder] Input validation passed', {
      orderId: params.orderId,
      marketId: params.marketId,
    });

    if (!this.walletAddress) {
      console.log('[ExchangeClient.cancelOrder] Wallet not initialized, initializing now...');
      await this.initialize();
      console.log('[ExchangeClient.cancelOrder] Wallet initialized', {
        walletAddress: this.walletAddress,
      });
    } else {
      console.log('[ExchangeClient.cancelOrder] Using existing wallet', {
        walletAddress: this.walletAddress,
      });
    }

    // Build payload following buildCancelOrderPayload pattern
    // EIP712 types: action, owner, address, orderId, marketId, nonce, deadline (7 fields)
    const walletAddress = this.getWalletAddress();
    const payload = {
      action: OpCancelOrder,
      orderId: params.orderId,
      marketId: params.marketId,
      address: walletAddress, // Required by EIP712 types
    };

    console.log('[ExchangeClient.cancelOrder] Payload built', {
      payload,
      action: OpCancelOrder,
    });

    const completeMessage = await this.buildCompleteMessage(payload);
    console.log('[ExchangeClient.cancelOrder] Complete message built', {
      action: completeMessage.action,
      orderId: completeMessage.orderId,
      marketId: completeMessage.marketId,
      address: completeMessage.address,
      owner: completeMessage.owner,
      nonce: completeMessage.nonce,
      deadline: completeMessage.deadline,
      hasAllFields: !!(completeMessage.action && completeMessage.orderId && completeMessage.marketId && completeMessage.owner && completeMessage.nonce && completeMessage.deadline),
    });

    // Generate signature using generateSingleSignature (fixes recovery ID issue)
    console.log('[ExchangeClient.cancelOrder] Generating signature...');
    const signature = await this.signPayload(completeMessage);
    console.log('[ExchangeClient.cancelOrder] Signature generated', {
      signatureLength: signature?.length,
      signaturePrefix: signature?.substring(0, 10) + '...',
    });

    // Build EIP712Tx structure (following buildEIP712Tx pattern)
    const eip712Tx = this.buildEIP712Tx(completeMessage, signature);
    console.log('[ExchangeClient.cancelOrder] EIP712Tx built', {
      eip712Tx: {
        txId: eip712Tx.txId,
        sigType: eip712Tx.sigType,
        threshold: eip712Tx.threshold,
        timestamp: eip712Tx.timestamp,
        deadline: eip712Tx.deadline,
        payloadAction: eip712Tx.payload?.action,
        payloadOrderId: eip712Tx.payload?.orderId,
        payloadMarketId: eip712Tx.payload?.marketId,
        signaturesCount: eip712Tx.signatures?.length,
        signer: eip712Tx.signatures?.[0]?.signer,
      },
    });

    // Submit to /router/v1/tx endpoint
    const baseURL = this.httpClient.getBaseURL ? this.httpClient.getBaseURL() : 'unknown';
    console.log('[ExchangeClient.cancelOrder] Submitting to /router/v1/tx endpoint...', {
      endpoint: '/router/v1/tx',
      baseURL,
      fullURL: `${baseURL}/router/v1/tx`,
    });
    
    try {
      const response = await this.httpClient.post('/router/v1/tx', eip712Tx);
      console.log('[ExchangeClient.cancelOrder] API response received', {
        status: response.statusCode,
        hasBody: !!response.body,
        responseBody: response.body,
        orderId: params.orderId,
      });
      return response.body;
    } catch (error) {
      // Check if this is a nonce error
      if (shouldRefreshNonce(error)) {
        console.log('[ExchangeClient.cancelOrder] Nonce error detected, refreshing nonce from server', {
          error: error instanceof Error ? error.message : String(error),
          orderId: params.orderId,
        });
        
        // Refresh nonce from server
        await this.refreshNonceFromServer();
        
        // Retry the request with new nonce
        const retryCompleteMessage = await this.buildCompleteMessage(payload);
        const retrySignature = await this.signPayload(retryCompleteMessage);
        const retryEip712Tx = this.buildEIP712Tx(retryCompleteMessage, retrySignature);
        
        console.log('[ExchangeClient.cancelOrder] Retrying with refreshed nonce', {
          newNonce: retryCompleteMessage.nonce,
        });
        
        const response = await this.httpClient.post('/router/v1/tx', retryEip712Tx);
        console.log('[ExchangeClient.cancelOrder] Retry successful', {
          status: response.statusCode,
          responseBody: response.body,
        });
        return response.body;
      }
      
      // Re-throw if not a nonce error
      console.error('[ExchangeClient.cancelOrder] API call failed', {
        error,
        errorMessage: error instanceof Error ? error.message : String(error),
        errorStack: error instanceof Error ? error.stack : undefined,
        orderId: params.orderId,
        marketId: params.marketId,
        walletAddress: this.walletAddress,
      });
      throw error;
    }
  }

  /**
   * Modify an order
   * Follows ModifyOrder pattern from dex_client.go
   * Uses EIP712Tx structure for Router v1 unified write endpoint
   */
  async modifyOrder(params: ModifyOrderParams): Promise<any> {
    // Input validation (following Go implementation)
    if (!params.orderId || params.orderId === '') {
      throw new Error('orderId cannot be empty');
    }
    if (!params.marketId || params.marketId.trim() === '') {
      throw new Error('marketId cannot be empty');
    }
    if (params.newPrice === undefined && params.newQuantity === undefined) {
      throw new Error('at least one of newPrice or newQuantity must be provided');
    }

    if (!this.walletAddress) {
      await this.initialize();
    }

    // Build payload following buildModifyOrderPayload pattern
    const payload: Record<string, any> = {
      action: OpModifyOrder,
      orderId: params.orderId,
      marketId: params.marketId,
    };

    // Add optional fields if provided (convert to satoshi if needed)
    if (params.newPrice !== undefined) {
      payload.newPrice = typeof params.newPrice === 'string'
        ? params.newPrice
        : toSatoshi(params.newPrice);
    }
    if (params.newQuantity !== undefined) {
      payload.newQuantity = typeof params.newQuantity === 'string'
        ? params.newQuantity
        : toSatoshi(params.newQuantity);
    }

    const completeMessage = await this.buildCompleteMessage(payload);

    // Generate signature using generateSingleSignature (fixes recovery ID issue)
    const signature = await this.signPayload(completeMessage);

    // Build EIP712Tx structure (following buildEIP712Tx pattern)
    const eip712Tx = this.buildEIP712Tx(completeMessage, signature);

    // Submit to /router/v1/tx endpoint
    const response = await this.httpClient.post('/router/v1/tx', eip712Tx);
    return response.body;
  }

  /**
   * Change leverage for a position
   * Follows ChangeLeverage pattern from dex_client.go
   * Uses EIP712Tx structure for Router v1 unified write endpoint
   */
  async changeLeverage(params: ChangeLeverageParams): Promise<any> {
    // Input validation
    if (!params.marketId || params.marketId.trim() === '') {
      throw new Error('marketId cannot be empty');
    }
    if (params.newLeverage === undefined) {
      throw new Error('newLeverage must be provided');
    }

    if (!this.walletAddress) {
      await this.initialize();
    }

    // Build payload following CreateChangeLeveragePayload pattern
    const payload: Record<string, any> = {
      action: OpChangeLeverage,
      marketId: params.marketId,
      newLeverage: typeof params.newLeverage === 'string'
        ? params.newLeverage
        : toSatoshi(params.newLeverage),
      address: this.getWalletAddress(),
    };

    // Add optional fields
    if (params.leverageType) {
      payload.leverageType = params.leverageType;
    }
    if (params.bucketId) {
      payload.bucketId = params.bucketId;
    }
    if (params.reason) {
      payload.reason = params.reason;
    }

    const completeMessage = await this.buildCompleteMessage(payload);
    const signature = await this.signPayload(completeMessage);
    const eip712Tx = this.buildEIP712Tx(completeMessage, signature);

    // Submit to /router/v1/tx endpoint
    const response = await this.httpClient.post('/router/v1/tx', eip712Tx);
    return response.body;
  }

  /**
   * Close a position
   * Follows ClosePosition pattern from dex_client.go
   * Uses EIP712Tx structure for Router v1 unified write endpoint
   */
  async closePosition(params: ClosePositionParams): Promise<any> {
    // Input validation
    if (!params.marketId || params.marketId.trim() === '') {
      throw new Error('marketId cannot be empty');
    }

    if (!this.walletAddress) {
      await this.initialize();
    }

    // Build payload
    const payload: Record<string, any> = {
      action: OpClosePosition,
      marketId: params.marketId,
      address: this.getWalletAddress(),
    };

    // Add optional fields
    if (params.positionId) {
      payload.positionId = params.positionId;
    }
    if (params.quantity !== undefined) {
      payload.quantity = typeof params.quantity === 'string'
        ? params.quantity
        : toSatoshi(params.quantity);
    }

    const completeMessage = await this.buildCompleteMessage(payload);
    const signature = await this.signPayload(completeMessage);
    const eip712Tx = this.buildEIP712Tx(completeMessage, signature);

    // Submit to /router/v1/tx endpoint
    const response = await this.httpClient.post('/router/v1/tx', eip712Tx);
    return response.body;
  }

  /**
   * Swap tokens
   * Follows Swap pattern from dex_client.go
   * Uses EIP712Tx structure for Router v1 unified write endpoint
   */
  async swap(params: SwapParams): Promise<any> {
    // Input validation
    if (!params.fromToken || params.fromToken === '') {
      throw new Error('fromToken cannot be empty');
    }
    if (!params.toToken || params.toToken === '') {
      throw new Error('toToken cannot be empty');
    }
    if (params.amountIn === undefined || (typeof params.amountIn === 'number' && params.amountIn <= 0)) {
      throw new Error('amountIn must be positive');
    }
    if (params.minAmountOut === undefined || (typeof params.minAmountOut === 'number' && params.minAmountOut <= 0)) {
      throw new Error('minAmountOut must be positive');
    }

    if (!this.walletAddress) {
      await this.initialize();
    }

    // Build payload following CreateSwapPayload pattern
    const payload: Record<string, any> = {
      action: OpSwap,
      fromToken: params.fromToken,
      toToken: params.toToken,
      amountIn: typeof params.amountIn === 'string'
        ? params.amountIn
        : toSatoshi(params.amountIn),
      minAmountOut: typeof params.minAmountOut === 'string'
        ? params.minAmountOut
        : toSatoshi(params.minAmountOut),
    };

    // Add optional path if provided
    if (params.path && params.path.length > 0) {
      payload.path = params.path;
    }

    const completeMessage = await this.buildCompleteMessage(payload);
    const signature = await this.signPayload(completeMessage);
    const eip712Tx = this.buildEIP712Tx(completeMessage, signature);

    // Submit to /router/v1/tx endpoint
    const response = await this.httpClient.post('/router/v1/tx', eip712Tx);
    return response.body;
  }

  /**
   * Change margin ratio
   * Follows ChangeMarginRatio pattern from dex_client.go
   * Uses EIP712Tx structure for Router v1 unified write endpoint
   */
  async changeMarginRatio(params: ChangeMarginRatioParams): Promise<any> {
    // Input validation
    if (!params.marketId || params.marketId.trim() === '') {
      throw new Error('marketId cannot be empty');
    }

    if (!this.walletAddress) {
      await this.initialize();
    }

    // Build payload
    const payload: Record<string, any> = {
      action: OpChangeMarginRatio,
      marketId: params.marketId,
      address: this.getWalletAddress(),
    };

    // Add optional bucketId if provided
    if (params.bucketId) {
      payload.bucketId = params.bucketId;
    }

    const completeMessage = await this.buildCompleteMessage(payload);
    const signature = await this.signPayload(completeMessage);
    const eip712Tx = this.buildEIP712Tx(completeMessage, signature);

    // Submit to /router/v1/tx endpoint
    const response = await this.httpClient.post('/router/v1/tx', eip712Tx);
    return response.body;
  }

  /**
   * Internal transfer between accounts
   * Follows InternTransfer pattern from dex_client.go
   * Uses EIP712Tx structure for Router v1 unified write endpoint
   */
  async internTransfer(params: InternTransferParams): Promise<any> {
    // Input validation
    if (!params.fromAccount || params.fromAccount === '') {
      throw new Error('fromAccount cannot be empty');
    }
    if (!params.toAccount || params.toAccount === '') {
      throw new Error('toAccount cannot be empty');
    }
    if (!params.tokenAddress || params.tokenAddress === '') {
      throw new Error('tokenAddress cannot be empty');
    }
    if (params.amount === undefined || (typeof params.amount === 'number' && params.amount <= 0)) {
      throw new Error('amount must be positive');
    }

    if (!this.walletAddress) {
      await this.initialize();
    }

    // Build payload following CreateInternTransferPayload pattern
    const payload: Record<string, any> = {
      action: OpInternTransfer,
      fromAccount: params.fromAccount,
      toAccount: params.toAccount,
      amount: typeof params.amount === 'string'
        ? params.amount
        : toSatoshi(params.amount),
      tokenAddress: params.tokenAddress,
      address: this.getWalletAddress(),
    };

    const completeMessage = await this.buildCompleteMessage(payload);
    const signature = await this.signPayload(completeMessage);
    const eip712Tx = this.buildEIP712Tx(completeMessage, signature);

    // Submit to /router/v1/tx endpoint
    const response = await this.httpClient.post('/router/v1/tx', eip712Tx);
    return response.body;
  }

  /**
   * Transfer margin between buckets
   * Follows the pattern from transfer_test.go
   * Uses flat request structure with EIP712 signature (not EIP712Tx)
   * 
   * @param params - Bucket transfer parameters
   * @returns API response
   */
  async bucketTransfer(params: BucketTransferParams): Promise<any> {
    // Input validation
    if (!params.fromBucketId || params.fromBucketId.trim() === '') {
      throw new Error('fromBucketId cannot be empty');
    }
    if (!params.toBucketId || params.toBucketId.trim() === '') {
      throw new Error('toBucketId cannot be empty');
    }
    if (!params.assetId || params.assetId.trim() === '') {
      throw new Error('assetId cannot be empty');
    }
    if (params.amount === undefined || (typeof params.amount === 'number' && params.amount <= 0)) {
      throw new Error('amount must be positive');
    }

    if (!this.walletAddress) {
      await this.initialize();
    }

    const walletAddress = this.getWalletAddress();
    const amountStr = typeof params.amount === 'string'
      ? params.amount
      : toSatoshi(params.amount);

    // Build payload for EIP712 signature (matches Go test payload structure)
    // Payload uses: owner, fromBucketId, toBucketId, asset, amount, nonce, deadline
    // Note: owner, nonce, and deadline are added by buildCompleteMessage
    const payload: Record<string, any> = {
      action: OpBankBucketTransfer,
      fromBucketId: params.fromBucketId,
      toBucketId: params.toBucketId,
      asset: params.assetId, // Use "asset" not "assetId" in payload
      amount: amountStr,
    };

    const completeMessage = await this.buildCompleteMessage(payload);
    const signature = await this.signPayload(completeMessage);

    // Build flat HTTP request structure (matches Go test reqBody structure)
    // HTTP request uses: address, source_bucket_id, target_bucket_id, amount, reason, timestamp, nonce, deadline, signature, chain_id
    const timestamp = Math.floor(Date.now() / 1000);
    const reqBody: Record<string, any> = {
      address: walletAddress,
      source_bucket_id: params.fromBucketId, // snake_case in HTTP body
      target_bucket_id: params.toBucketId, // snake_case in HTTP body
      amount: amountStr,
      reason: "sdk_transfer", // Optional field, present in Go test
      timestamp: timestamp,
      nonce: completeMessage.nonce,
      deadline: completeMessage.deadline,
      signature: signature,
      chain_id: this.chainID.toString(),
    };

    // Submit to /bucket/v1/bucket/transfer endpoint with flat structure
    const response = await this.httpClient.post('/bucket/v1/bucket/transfer', reqBody);
    return response.body;
  }

  /**
   * Extract spot balance to bucket (bank → bucket)
   * Follows the pattern from bank_to_bucket_test.go
   * Uses flat request structure with EIP712 signature (not EIP712Tx)
   * 
   * @param params - Extract to bucket parameters
   * @returns API response
   */
  async extractToBucket(params: ExtractToBucketParams): Promise<any> {
    // Input validation
    if (!params.bucketId || params.bucketId.trim() === '') {
      throw new Error('bucketId cannot be empty');
    }
    if (!params.assetId || params.assetId.trim() === '') {
      throw new Error('assetId cannot be empty');
    }
    if (params.amount === undefined || (typeof params.amount === 'number' && params.amount <= 0)) {
      throw new Error('amount must be positive');
    }

    if (!this.walletAddress) {
      await this.initialize();
    }

    const walletAddress = this.getWalletAddress();
    const amountStr = typeof params.amount === 'string'
      ? params.amount
      : toSatoshi(params.amount);

    // Build payload for EIP712 signature (matches Go test payload structure)
    // Payload uses: owner, bucketId (camelCase), asset, amount, nonce, deadline
    // Note: owner, nonce, and deadline are added by buildCompleteMessage
    const payload: Record<string, any> = {
      action: OpBankExtractToBucket,
      bucketId: params.bucketId,
      asset: params.assetId, // Use "asset" not "assetId" in payload
      amount: amountStr,
    };

    const completeMessage = await this.buildCompleteMessage(payload);
    const signature = await this.signPayload(completeMessage);

    // Build flat HTTP request structure (matches Go test reqBody structure)
    // HTTP request uses: address, bucket_id (snake_case), asset, amount, timestamp, nonce, deadline, signature, chain_id
    const timestamp = Math.floor(Date.now() / 1000);
    const reqBody: Record<string, any> = {
      address: walletAddress,
      bucket_id: params.bucketId, // snake_case in HTTP body
      asset: params.assetId, // Use "asset" not "assetId"
      amount: amountStr,
      timestamp: timestamp,
      nonce: completeMessage.nonce,
      deadline: completeMessage.deadline,
      signature: signature,
      chain_id: this.chainID.toString(),
    };

    // Submit to /bank/v1/extract-to-bucket endpoint with flat structure
    const response = await this.httpClient.post('/bank/v1/extract-to-bucket', reqBody);
    return response.body;
  }

  /**
   * Withdraw margin from bucket to bank (bucket → bank)
   * Follows the same pattern as extractToBucket
   * Uses flat request structure with EIP712 signature (not EIP712Tx)
   * 
   * @param params - Withdraw from bucket parameters
   * @returns API response
   */
  async withdrawFromBucket(params: WithdrawFromBucketParams): Promise<any> {
    // Input validation
    if (!params.bucketId || params.bucketId.trim() === '') {
      throw new Error('bucketId cannot be empty');
    }
    if (!params.assetId || params.assetId.trim() === '') {
      throw new Error('assetId cannot be empty');
    }
    if (params.amount === undefined || (typeof params.amount === 'number' && params.amount <= 0)) {
      throw new Error('amount must be positive');
    }

    if (!this.walletAddress) {
      await this.initialize();
    }

    const walletAddress = this.getWalletAddress();
    const amountStr = typeof params.amount === 'string'
      ? params.amount
      : toSatoshi(params.amount);

    // Build payload for EIP712 signature (matches Go test payload structure)
    // Payload uses: owner, bucketId (camelCase), asset, amount, nonce, deadline
    // Note: owner, nonce, and deadline are added by buildCompleteMessage
    const payload: Record<string, any> = {
      action: OpBankWithdrawFromBucket,
      bucketId: params.bucketId,
      asset: params.assetId, // Use "asset" not "assetId" in payload
      amount: amountStr,
    };

    const completeMessage = await this.buildCompleteMessage(payload);
    const signature = await this.signPayload(completeMessage);

    // Build flat HTTP request structure (matches Go test reqBody structure)
    // HTTP request uses: address, bucket_id (snake_case), asset, amount, timestamp, nonce, deadline, signature, chain_id
    const timestamp = Math.floor(Date.now() / 1000);
    const reqBody: Record<string, any> = {
      address: walletAddress,
      bucket_id: params.bucketId, // snake_case in HTTP body
      asset: params.assetId, // Use "asset" not "assetId"
      amount: amountStr,
      timestamp: timestamp,
      nonce: completeMessage.nonce,
      deadline: completeMessage.deadline,
      signature: signature,
      chain_id: this.chainID.toString(),
    };

    // Submit to /bank/v1/withdraw-from-bucket endpoint with flat structure
    const response = await this.httpClient.post('/bank/v1/withdraw-from-bucket', reqBody);
    return response.body;
  }

  /**
   * Mint new tokens
   * Uses EIP712Tx structure for Router v1 unified write endpoint
   * 
   * @param params - Bank mint parameters
   * @returns API response
   */
  async bankMint(params: BankMintParams): Promise<any> {
    // Input validation
    if (!params.assetId || params.assetId.trim() === '') {
      throw new Error('assetId cannot be empty');
    }
    if (params.amount === undefined || (typeof params.amount === 'number' && params.amount <= 0)) {
      throw new Error('amount must be positive');
    }

    if (!this.walletAddress) {
      await this.initialize();
    }

    const amountStr = typeof params.amount === 'string'
      ? params.amount
      : toSatoshi(params.amount);

    // Build payload following bank::mint EIP712 types
    // Payload uses: owner, asset, amount, nonce, deadline
    const payload: Record<string, any> = {
      action: OpBankMint,
      asset: params.assetId, // Use "asset" not "assetId" in payload
      amount: amountStr,
    };

    const completeMessage = await this.buildCompleteMessage(payload);
    const signature = await this.signPayload(completeMessage);
    const eip712Tx = this.buildEIP712Tx(completeMessage, signature);

    // Submit to /router/v1/tx endpoint
    const response = await this.httpClient.post('/router/v1/tx', eip712Tx);
    return response.body;
  }

  /**
   * Direct address-to-address asset transfer
   * Uses EIP712Tx structure for Router v1 unified write endpoint
   * 
   * @param params - Bank transfer parameters
   * @returns API response
   */
  async bankTransfer(params: BankTransferParams): Promise<any> {
    // Input validation
    if (!params.to || params.to.trim() === '') {
      throw new Error('to address cannot be empty');
    }
    if (!params.assetId || params.assetId.trim() === '') {
      throw new Error('assetId cannot be empty');
    }
    if (params.amount === undefined || (typeof params.amount === 'number' && params.amount <= 0)) {
      throw new Error('amount must be positive');
    }

    if (!this.walletAddress) {
      await this.initialize();
    }

    const amountStr = typeof params.amount === 'string'
      ? params.amount
      : toSatoshi(params.amount);

    // Build payload following bank::transfer EIP712 types
    // Payload uses: owner, to, asset, amount, nonce, deadline
    const payload: Record<string, any> = {
      action: OpBankTransfer,
      to: params.to,
      asset: params.assetId, // Use "asset" not "assetId" in payload
      amount: amountStr,
    };

    const completeMessage = await this.buildCompleteMessage(payload);
    const signature = await this.signPayload(completeMessage);
    const eip712Tx = this.buildEIP712Tx(completeMessage, signature);

    // Submit to /router/v1/tx endpoint
    const response = await this.httpClient.post('/router/v1/tx', eip712Tx);
    return response.body;
  }
}
